'use client';

import { c as _c } from "react/compiler-runtime";
import { getCurrencySymbol } from '../currency';
import { Input } from './Input';
import type { CurrencyId } from '../currency';
export interface AmountInputProps extends Omit<React.ComponentPropsWithoutRef<'input'>, 'value' | 'onChange' | 'prefix' | 'type' | 'ref'> {
  value: string;
  onChange: (value: string) => void;
  /**
   * Currency code resolved to a symbol for the prefix (e.g. 'usd' → '$'). Omit it for a currency
   * outside the registry, so the input shows no symbol rather than a wrong one.
   */
  currency?: CurrencyId;
  /** Maximum decimal places allowed (default: 2). */
  decimals?: number;
  /** Allow negative amounts (default: false). */
  allowNegative?: boolean;
}

/**
 * String-based amount input with a currency symbol prefix.
 * Restricts input to valid decimal numbers and formats on blur
 * with thousands separators.
 *
 * The value is always a string (e.g. "500.00"). Suitable for APIs that
 * accept decimal amount strings rather than floats.
 */
export function AmountInput(t0) {
  const $ = _c(43);
  let className;
  let currency;
  let disabled;
  let id;
  let onChange;
  let restProps;
  let t1;
  let t2;
  let t3;
  let value;
  if ($[0] !== t0) {
    ({
      value,
      onChange,
      currency,
      decimals: t1,
      allowNegative: t2,
      placeholder: t3,
      disabled,
      id,
      className,
      ...restProps
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = currency;
    $[3] = disabled;
    $[4] = id;
    $[5] = onChange;
    $[6] = restProps;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = value;
  } else {
    className = $[1];
    currency = $[2];
    disabled = $[3];
    id = $[4];
    onChange = $[5];
    restProps = $[6];
    t1 = $[7];
    t2 = $[8];
    t3 = $[9];
    value = $[10];
  }
  const decimals = t1 === undefined ? 2 : t1;
  const allowNegative = t2 === undefined ? false : t2;
  const placeholder = t3 === undefined ? "0.00" : t3;
  let t4;
  if ($[11] !== currency) {
    t4 = currency ? getCurrencySymbol(currency) : undefined;
    $[11] = currency;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const symbol = t4;
  let t5;
  if ($[13] !== allowNegative || $[14] !== decimals) {
    t5 = function sanitize(raw) {
      let cleaned = raw.replace(/[,\s]/g, "");
      const isNegative = allowNegative && cleaned.startsWith("-");
      if (isNegative) {
        cleaned = cleaned.slice(1);
      }
      const parts = cleaned.replace(/[^\d.]/g, "").split(".");
      let sanitized = parts[0] ?? "";
      if (parts.length > 1 && decimals > 0) {
        sanitized = sanitized + ("." + parts.slice(1).join("").slice(0, decimals));
      }
      return (isNegative && sanitized ? "-" : "") + sanitized;
    };
    $[13] = allowNegative;
    $[14] = decimals;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  const sanitize = t5;
  let t6;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = function formatWithSeparators(val) {
      if (!val) {
        return "";
      }
      const isNegative_0 = val.startsWith("-");
      const abs = isNegative_0 ? val.slice(1) : val;
      const [integer, decimal] = abs.split(".");
      const formatted = (integer ?? "").replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      const result = decimal !== undefined ? `${formatted}.${decimal}` : formatted;
      return (isNegative_0 ? "-" : "") + result;
    };
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const formatWithSeparators = t6;
  let t7;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = function stripFormatting(formatted_0) {
      return formatted_0.replace(/,/g, "");
    };
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const stripFormatting = t7;
  let t8;
  if ($[18] !== allowNegative || $[19] !== onChange || $[20] !== sanitize) {
    t8 = function handleChange(e) {
      const raw_0 = e.target.value;
      if (raw_0 === "" || raw_0 === "-") {
        onChange(raw_0 === "-" && allowNegative ? "-" : "");
        return;
      }
      const sanitized_0 = sanitize(raw_0);
      onChange(sanitized_0);
    };
    $[18] = allowNegative;
    $[19] = onChange;
    $[20] = sanitize;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const handleChange = t8;
  let handleBlur;
  if ($[22] !== decimals || $[23] !== onChange || $[24] !== value) {
    handleBlur = function handleBlur() {
      if (!value || value === "-") {
        return;
      }
      onChange(padDecimals(value));
    };
    function padDecimals(val_0) {
      const isNegative_1 = val_0.startsWith("-");
      const [t9, t10] = (isNegative_1 ? val_0.slice(1) : val_0).split(".");
      const integer_0 = t9 === undefined ? "" : t9;
      const fraction = t10 === undefined ? "" : t10;
      const whole = integer_0.replace(/^0+(?=\d)/, "") || "0";
      const padded = decimals > 0 ? `${whole}.${fraction.slice(0, decimals).padEnd(decimals, "0")}` : whole;
      const isZero = !/[1-9]/.test(padded);
      return (isNegative_1 && !isZero ? "-" : "") + padded;
    }
    $[22] = decimals;
    $[23] = onChange;
    $[24] = value;
    $[25] = handleBlur;
  } else {
    handleBlur = $[25];
  }
  let t9;
  if ($[26] !== allowNegative || $[27] !== decimals || $[28] !== value) {
    t9 = function handleKeyDown(e_0) {
      const allowedKeys = ["Backspace", "Delete", "Tab", "Escape", "Enter", "ArrowLeft", "ArrowRight", "Home", "End"];
      if (allowedKeys.includes(e_0.key)) {
        return;
      }
      if ((e_0.ctrlKey || e_0.metaKey) && ["a", "c", "v", "x"].includes(e_0.key.toLowerCase())) {
        return;
      }
      if (/^\d$/.test(e_0.key)) {
        const dotIndex = value.indexOf(".");
        if (dotIndex !== -1) {
          const input = e_0.target as HTMLInputElement;
          const cursorPos = input.selectionStart ?? value.length;
          const decimalsAfterDot = value.length - dotIndex - 1;
          if (cursorPos > dotIndex && decimalsAfterDot >= decimals) {
            e_0.preventDefault();
            return;
          }
        }
        return;
      }
      if (e_0.key === "." && decimals > 0 && !value.includes(".")) {
        return;
      }
      if (e_0.key === "-" && allowNegative && !value.includes("-")) {
        return;
      }
      e_0.preventDefault();
    };
    $[26] = allowNegative;
    $[27] = decimals;
    $[28] = value;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  const handleKeyDown = t9;
  const displayValue = formatWithSeparators(value);
  let t10;
  if ($[30] !== handleChange) {
    t10 = e_1 => {
      const rawInput = stripFormatting(e_1.target.value);
      handleChange({
        ...e_1,
        target: {
          ...e_1.target,
          value: rawInput
        }
      } as React.ChangeEvent<HTMLInputElement>);
    };
    $[30] = handleChange;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  let t11;
  if ($[32] !== className || $[33] !== disabled || $[34] !== displayValue || $[35] !== handleBlur || $[36] !== handleKeyDown || $[37] !== id || $[38] !== placeholder || $[39] !== restProps || $[40] !== symbol || $[41] !== t10) {
    t11 = <Input {...restProps} className={className} disabled={disabled} id={id} inputMode="decimal" onBlur={handleBlur} onChange={t10} onKeyDown={handleKeyDown} placeholder={placeholder} prefix={symbol} showBorders={true} type="text" value={displayValue} />;
    $[32] = className;
    $[33] = disabled;
    $[34] = displayValue;
    $[35] = handleBlur;
    $[36] = handleKeyDown;
    $[37] = id;
    $[38] = placeholder;
    $[39] = restProps;
    $[40] = symbol;
    $[41] = t10;
    $[42] = t11;
  } else {
    t11 = $[42];
  }
  return t11;
}